import { type PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { getAllClients, getClientsPaged } from "../service/client.service";
import type { RootState } from "../store";
import type { TClientForm, TClientScope, Tdsm } from "../types/index.type";
import { sortClientsByName } from "../utils/datamanipulation.utils";

export const SIDEBAR_PAGE_SIZE = 50;

// Legacy full fetch — now only hydrates the byId cache (e.g. refresh after a share). No array writes.
export const fetchAllClients = createAsyncThunk("client/fetchAllClients", async (_, { rejectWithValue }) => {
  try {
    const response = await getAllClients();
    const ownedClients = sortClientsByName(response.data.owned || []);
    const sharedClients = sortClientsByName(response.data.shared || []);
    return { ownedClients, sharedClients };
  } catch (error) {
    return rejectWithValue(error);
  }
});

export type SidebarScope = Exclude<TClientScope, "owned">;

export type TClientSort = "name" | "recent";
const SORT_STORAGE_KEY = "clients-sort-by";

const readStoredSort = (): TClientSort => {
  try {
    return localStorage.getItem(SORT_STORAGE_KEY) === "recent" ? "recent" : "name";
  } catch {
    return "name";
  }
};

/** Persist the sort preference across sessions (reducers stay pure; the toggle calls this). */
export const persistSortPreference = (sort: TClientSort) => {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, sort);
  } catch {
    // Private mode / storage full: the toggle still works for this session.
  }
};

// Server-paginated sidebar section fetch (all OR shared). Drives infinite scroll + search.
// The sort mode is read from state (single source), so every caller — sidebar, pickers,
// retries — always paginates the ordering the user selected.
export const fetchClientSidebarPage = createAsyncThunk(
  "client/fetchSidebarPage",
  async (
    args: { scope: SidebarScope; offset: number; search: string; reset: boolean },
    { getState, rejectWithValue },
  ) => {
    try {
      const { scope, offset, search } = args;
      const sortBy = (getState() as RootState).client.sidebar.sortBy;
      const response = await getClientsPaged({
        scope,
        offset,
        search,
        page_size: SIDEBAR_PAGE_SIZE,
        ...(sortBy === "recent" ? { ordering: "recent" } : {}),
      });
      return { ...args, ...response.data };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

type SidebarSection = { ids: number[]; offset: number; hasMore: boolean; loading: boolean; error: boolean };
const emptySection = (): SidebarSection => ({ ids: [], offset: 0, hasMore: false, loading: false, error: false });

// Merge clients into the byId display cache (never the source of truth for existence — only hydration).
const indexClients = (byId: Record<number, TClientForm>, list: TClientForm[]) => {
  for (const c of list) {
    if (c?.id != null) byId[c.id] = c;
  }
};

const idsOf = (list: TClientForm[]): number[] => list.map((c) => c.id).filter((id): id is number => id != null);

// Define a type for the slice state
type ClientState = {
  // Normalized display cache + paginated sidebar state (replaced the old in-memory list arrays).
  byId: Record<number, TClientForm>;
  recentIds: number[];
  // Rows that arrived with the most recent EHR sync, so the sidebar can tag them.
  // The sync endpoint returns counts, not ids — these come from diffing the list.
  recentlySyncedIds: number[];
  sidebar: { query: string; sortBy: TClientSort; all: SidebarSection; shared: SidebarSection };
  clientForm: TClientForm;
  dsm: Tdsm[];
  customizationList: any[];
  note_type_instructions: {
    [key: number]: string | { note: string; is_system: boolean };
  };
  suggestion: string;
  isEditing: boolean;
};

// Initial state
const initialState: ClientState = {
  byId: {},
  recentIds: [],
  recentlySyncedIds: [],
  sidebar: { query: "", sortBy: readStoredSort(), all: emptySection(), shared: emptySection() },
  dsm: [],
  clientForm: {
    name: "",
    category_type: 0,
    gender_type: 0,
    language_type: 0,
    diagnosis_ids: [],
    refer_type: 0,
    mrn: "",
    date_of_birth: null,
    context: "",
  },
  customizationList: [],
  note_type_instructions: {},
  suggestion: "",
  isEditing: false,
};

// Slice
const clientSlice = createSlice({
  name: "client",
  initialState,
  reducers: {
    // Merge clients into the byId hydration cache.
    cacheClients: (state, action: PayloadAction<TClientForm[]>) => {
      indexClients(state.byId, action.payload);
    },
    // Recently-used clients for picker empty-focus (derived from recent notes' clients).
    setRecentClients: (state, action: PayloadAction<TClientForm[]>) => {
      indexClients(state.byId, action.payload);
      state.recentIds = idsOf(action.payload);
    },
    setSidebarQuery: (state, action: PayloadAction<string>) => {
      state.sidebar.query = action.payload;
    },
    setRecentlySyncedIds: (state, action: PayloadAction<number[]>) => {
      state.recentlySyncedIds = action.payload;
    },
    // The "new from sync" dot is a "you haven't looked at this yet" marker, so
    // opening the patient is what clears it (the next sync resets the whole set).
    clearRecentlySyncedId: (state, action: PayloadAction<number>) => {
      state.recentlySyncedIds = state.recentlySyncedIds.filter((id) => id !== action.payload);
    },
    // Sort mode for the shared browse list (sidebar + every picker). Callers refetch the
    // sections after dispatching; persistence to localStorage happens at the toggle.
    setSidebarSort: (state, action: PayloadAction<TClientSort>) => {
      state.sidebar.sortBy = action.payload;
    },
    setClientForm: (state, action: PayloadAction<TClientForm>) => {
      state.clientForm = action.payload;
    },
    setDsm: (state, action: PayloadAction<{ id: number; code: string; name: string }[]>) => {
      state.dsm = action.payload;
    },
    setCustomizationList: (state, action: PayloadAction<any[]>) => {
      state.customizationList = action.payload;
    },
    setNoteTypeInstructionNote: (
      state,
      action: PayloadAction<{
        [key: number]: string | { note: string; is_system: boolean };
      }>,
    ) => {
      state.note_type_instructions = action.payload;
    },
    setIsEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    resetClientForm: (state) => {
      state.clientForm = { ...initialState.clientForm };
    },
    setSuggestion: (state, action: PayloadAction<string>) => {
      state.suggestion = action.payload;
    },
    clearSuggestion: (state) => {
      state.suggestion = "";
    },
    // Optimistic CRUD — patch byId + sidebar in place (no full-list refetch).
    addClient: (state, action: PayloadAction<TClientForm>) => {
      const newClient = action.payload;
      if (newClient.id == null) return;
      state.byId[newClient.id] = newClient;
      const all = state.sidebar.all;
      if (all.ids.includes(newClient.id)) return;
      if (state.sidebar.sortBy === "recent") {
        // Newest-first: the fresh client IS the top row — optimistic placement == server truth.
        all.ids.unshift(newClient.id);
        return;
      }
      // Alphabetical: insert at the sorted position among the LOADED rows so the placement
      // survives a refetch (no more "added at top, jumps to the middle on refresh"). If it
      // sorts past the loaded window and more pages exist, leave it to arrive with its page —
      // it's cached in byId either way.
      const name = (newClient.name || "").toLowerCase();
      const at = all.ids.findIndex((id) => ((state.byId[id]?.name || "").toLowerCase() || "") > name);
      if (at !== -1) all.ids.splice(at, 0, newClient.id);
      else if (!all.hasMore) all.ids.push(newClient.id);
    },
    removeClient: (state, action: PayloadAction<number>) => {
      const clientId = action.payload;
      delete state.byId[clientId];
      state.sidebar.all.ids = state.sidebar.all.ids.filter((id) => id !== clientId);
      state.sidebar.shared.ids = state.sidebar.shared.ids.filter((id) => id !== clientId);
      state.recentIds = state.recentIds.filter((id) => id !== clientId);
      state.recentlySyncedIds = state.recentlySyncedIds.filter((id) => id !== clientId);
    },
    updateClientInList: (state, action: PayloadAction<TClientForm>) => {
      const updatedClient = action.payload;
      if (updatedClient.id != null) {
        state.byId[updatedClient.id] = updatedClient;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllClients.fulfilled, (state, action) => {
      const { ownedClients, sharedClients } = action.payload;
      indexClients(state.byId, [...ownedClients, ...sharedClients]);
    });
    builder.addCase(fetchClientSidebarPage.pending, (state, action) => {
      const section = state.sidebar[action.meta.arg.scope];
      section.loading = true;
      section.error = false;
      if (action.meta.arg.reset) {
        section.ids = [];
        section.offset = 0;
        section.hasMore = false;
      }
    });
    builder.addCase(fetchClientSidebarPage.fulfilled, (state, action) => {
      const { scope, reset, results, hasMore, next_offset, offset } = action.payload;
      const section = state.sidebar[scope];
      indexClients(state.byId, results);
      const newIds = idsOf(results);
      section.ids = reset ? newIds : [...section.ids, ...newIds.filter((id) => !section.ids.includes(id))];
      section.offset = next_offset ?? offset + results.length;
      section.hasMore = hasMore;
      section.loading = false;
      section.error = false;
    });
    builder.addCase(fetchClientSidebarPage.rejected, (state, action) => {
      const section = state.sidebar[action.meta.arg.scope];
      section.loading = false;
      section.error = true;
    });
  },
});

// Actions
export const {
  setClientForm,
  cacheClients,
  setRecentClients,
  setSidebarQuery,
  setSidebarSort,
  setRecentlySyncedIds,
  clearRecentlySyncedId,
  resetClientForm,
  setDsm,
  setIsEditing,
  setNoteTypeInstructionNote,
  setCustomizationList,
  setSuggestion,
  clearSuggestion,
  addClient,
  removeClient,
  updateClientInList,
} = clientSlice.actions;

// Reload the sidebar's first page after an EHR sync and tag whatever was not
// already in it. The sync endpoint reports counts only, so the rows that arrived
// are learned by diffing the list against the pre-refresh window.
export const refreshClientsAfterSync = createAsyncThunk(
  "client/refreshAfterSync",
  async (_, { dispatch, getState }) => {
    const { sidebar } = (getState() as RootState).client;
    const before = new Set(sidebar.all.ids);
    const action = await dispatch(
      fetchClientSidebarPage({ scope: "all", offset: 0, search: sidebar.query, reset: true }),
    );
    if (!fetchClientSidebarPage.fulfilled.match(action)) return;
    dispatch(setRecentlySyncedIds(idsOf(action.payload.results).filter((id) => !before.has(id))));
  },
);

// Selectors
export const selectClientById =
  (id?: number | null) =>
  (state: RootState): TClientForm | undefined =>
    id != null ? state.client.byId[id] : undefined;

export const selectClientByUuid =
  (uuid?: string | null) =>
  (state: RootState): TClientForm | undefined =>
    uuid ? Object.values(state.client.byId).find((c) => c.uuid === uuid) : undefined;

// Memoized so it returns a stable array reference (avoids the "selector returns a new reference"
// re-render warning when consumed via useSelector).
export const selectRecentClients = createSelector(
  (state: RootState) => state.client.recentIds,
  (state: RootState) => state.client.byId,
  (recentIds, byId): TClientForm[] => recentIds.map((id) => byId[id]).filter((c): c is TClientForm => Boolean(c)),
);

export const selectSidebarSection =
  (scope: SidebarScope) =>
  (state: RootState): SidebarSection =>
    state.client.sidebar[scope];

export const selectSidebarQuery = (state: RootState): string => state.client.sidebar.query;

export const selectRecentlySyncedIds = (state: RootState): number[] => state.client.recentlySyncedIds;

export const selectSidebarSort = (state: RootState): TClientSort => state.client.sidebar.sortBy;

// Reducer
export default clientSlice.reducer;
